import { FC, useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../context/notificationContext";
import { useUserSettings } from "../../context/userSettingsContext";
import useDataInitializer from "../../hooks/useDataInitializer";
import { useDebounce } from "../../hooks/useDebounce";
import useListFilters from "../../hooks/useListFilters";
import { useI18n } from "../../hooks/useTranslations";
import { useCarDriversApi } from "../../http/carDrivers/api";
import { useCarPlanningsApi } from "../../http/carPlannings/api";
import {
  CarPlanning,
  CarPlanningStatus,
  getCarPlanningStates,
  getCarPlanningTypes,
} from "../../http/carPlannings/types";
import { CarPurchase, getCarPurchaseStates } from "../../http/carPurchases/types";
import { PaginationQuery } from "../../types";
import { downloadFileFromData, formatCurrency, formatDateNumeric, isWithinLast48h } from "../../types/utils";
import CarDriverForm, { CarDriverFormBody } from "../carDrivers/CarDriverForm";
import SvgAdd from "../icons/Add";
import SvgDelete from "../icons/Delete";
import SvgEdit from "../icons/Edit";
import SvgHistory from "../icons/History";
import SvgShoppingCart from "../icons/ShoppingCart";
import SvgTaskAlt from "../icons/TaskAlt";
import Button from "../ui/Button";
import TextColumnFilter from "../ui/columnFilters/TextColumnFilter";
import ExportButton from "../ui/ExportButton";
import Header from "../ui/Header";
import { Action } from "../ui/ListItemMenu";
import ModalDialog from "../ui/ModalDialog";
import NewItemIcon from "../ui/NewItemIcon";
import Panel from "../ui/Panel";
import { State } from "../ui/State";
import Table from "../ui/Table";
import Tooltip from "../ui/Tooltip";
import CarPlanningHistoryDialog from "./CarPlanningHistoryDialog";
import CarPlanningImportButton from "./CarPlanningImportButton";
import DeleteCarPlanningDialog from "./DeleteCarPlanningDialog";

const CarPlannings: FC = () => {
  const i18n = useI18n();
  const carPlanningsApi = useCarPlanningsApi();
  const carDriversApi = useCarDriversApi();
  const userSettings = useUserSettings();
  const filters = useListFilters({ isSearchable: true });
  const debouncedValue = useDebounce(filters.state.q);
  const aborter = useRef(new AbortController());
  const dispatch = useContext(NotificationDispatch);
  const navigate = useNavigate();

  const [itemToDelete, setItemToDelete] = useState<CarPlanning>();
  const [itemToShowVersions, setItemToShowVersions] = useState<CarPlanning>();
  const [openNewCarDriver, setOpenNewCarDriver] = useState<CarPlanning>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const query: PaginationQuery = {
    ...filters.state,
    q: debouncedValue,
  };

  const navToEdit = (item: CarPlanning) => navigate(`/admin/car_plannings/${item.id}`);

  const navToOrderItem = (item: CarPlanning) => navigate(`/admin/car_purchases/new?car_planning_id=${item.id}`);
  const navToCarPurchase = (item: CarPurchase | undefined) =>
    navigate(`/admin/car_purchases/${item?.id}?redirect_url=${encodeURIComponent(window.location.pathname)}`);

  const carPlanningStates = getCarPlanningStates(i18n.t);
  const carPurchaseStates = getCarPurchaseStates(i18n.t);
  const types = getCarPlanningTypes(i18n.t);

  const isDisabledStatus = (status?: CarPlanningStatus) =>
    status === "assigned" || status === "ordered" || status === "cancelled";

  const getRowActions = (item: CarPlanning): Action[] => {
    const actions: Action[] = [];

    if (userSettings.permissions.carPlannings.update) {
      actions.push({
        title: i18n.t.common.edit,
        onClick: () => navToEdit(item),
        icon: SvgEdit,
      });
    }

    if (!item.carPurchase && userSettings.permissions.carPurchases.insert) {
      actions.push({
        title: i18n.t.common.orderImperativ,
        isDisabled: isDisabledStatus(item.status) || !item.subsidiary,
        visible: item.type == "new-purchase" || item.type == "new-leasing",
        onClick: () => navToOrderItem(item),
        icon: SvgTaskAlt,
      });
    }

    if (
      item.carPurchase &&
      (userSettings.permissions.carPurchases.read || userSettings.permissions.carPurchases.update)
    ) {
      actions.push({
        title: `${i18n.t.common.purchase} (${userSettings.permissions.carPurchases.update ? i18n.t.common.edit : i18n.t.common.view})`,
        visible: !!item.carPurchase,
        onClick: () => navToCarPurchase(item.carPurchase),
        icon: SvgShoppingCart,
      });
    }

    if (userSettings.permissions.carDrivers.insert) {
      actions.push({
        title: i18n.t.common.assign,
        isDisabled: isDisabledStatus(item.status) || !item.subsidiary,
        visible: item.type == "re-assignment",
        onClick: () => setOpenNewCarDriver(item),
        icon: SvgTaskAlt,
      });
    }

    actions.push({ title: i18n.t.common.history, onClick: () => setItemToShowVersions(item), icon: SvgHistory });

    if (userSettings.permissions.carPlannings.delete) {
      actions.push({
        title: i18n.t.common.delete,
        onClick: () => setItemToDelete(item),
        icon: SvgDelete,
        isDivider: true,
        color: "red",
      });
    }

    return actions;
  };

  const result = carPlanningsApi.useAdminCarPlannings(query, aborter.current.signal);

  useEffect(() => {
    result.error && dispatch(showErrorNotification(i18n, result.error));
  }, [result.error]);

  const [data] = useDataInitializer(result.data, result.error);

  const filterOptions = {
    get: carPlanningsApi.getFieldFilters,
    update: filters.setFilters,
    getSelected: filters.getFilters,
  };

  const orderOptions = {
    by: filters.state.orderBy,
    desc: filters.state.desc,
    setBy: filters.setOrder,
  };

  const onSubmit = async (formData: CarDriverFormBody) => {
    setIsSubmitting(true);
    try {
      await carDriversApi.insertAdminCarDriver({
        carId: formData.car.id,
        driverId: formData.driver.id,
        startDate: formData.startDate,
        endDate: formData.endDate,
        typeOfUse: formData.typeOfUse.value,
        carPlanningId: openNewCarDriver?.id,
        sendMileageEmail: formData.sendMileageEmail,
      });
      dispatch(showSuccessNotification(i18n.t));
    } catch (error) {
      dispatch(showErrorNotification(i18n, error));
    } finally {
      setOpenNewCarDriver(undefined);
      setIsSubmitting(false);
    }
  };

  const statesMap: Record<CarPlanningStatus, "red" | "blue" | "green" | "orange" | "yellow"> = {
    assigned: "green",
    "to-assign": "orange",
    ordered: "blue",
    "to-order": "yellow",
    cancelled: "red",
  };

  const reload = async () => {
    try {
      await carPlanningsApi.reloadLines();
      dispatch(showSuccessNotification(i18n.t));
    } catch (error) {
      dispatch(showErrorNotification(i18n, error));
    }
  };

  return (
    <>
      <Header title={i18n.t.carPlannings.plural} />
      <Panel>
        <Table<CarPlanning>
          filters={filters}
          searchOptions={{
            onChange: () => {
              aborter.current.abort();
              aborter.current = new AbortController();
            },
          }}
          buttons={{
            main: (
              <>
                <Button buttonProps={{ onClick: () => navigate("/admin/car_plannings/new") }} glyph={SvgAdd} />
                <CarPlanningImportButton />
              </>
            ),
            secondary: (
              <>
                <Button buttonProps={{ onClick: () => reload() }}>{i18n.t.common.reloadData}</Button>
                <ExportButton.Excel
                  onClick={async () => {
                    const r = await carPlanningsApi.exportFile(query, aborter.current.signal, false);
                    r && downloadFileFromData(r?.data, "car_plannings", r?.contentType);
                  }}
                />
                <ExportButton.Csv
                  onClick={async () => {
                    const r = await carPlanningsApi.exportFile(query, aborter.current.signal, true);
                    r && downloadFileFromData(r?.data, "car_plannings", r?.contentType);
                  }}
                />
              </>
            ),
          }}
          head={
            <>
              <Table.Th
                title={i18n.t.common.positionCode}
                fieldName="carPlanning.number"
                filters={filterOptions}
                filter={TextColumnFilter}
                order={orderOptions}
              />

              <Table.Th
                title={i18n.t.common.positionName}
                fieldName="carPlanning.position.name"
                filters={filterOptions}
                filter={TextColumnFilter}
                order={orderOptions}
              />

              <Table.Th
                title={i18n.t.subsidiaries.singular}
                fieldName="carPlanning.subsidiary.name"
                filters={filterOptions}
                filter={TextColumnFilter}
                order={orderOptions}
              />

              <Table.Th
                title={i18n.t.common.semiramisOrganizationCode}
                fieldName="carPlanning.subsidiaryCode"
                filters={filterOptions}
                filter={TextColumnFilter}
                order={orderOptions}
              />

              <Table.Th
                title={i18n.t.common.costCenter}
                fieldName="carPlanning.costCenter"
                filters={filterOptions}
                filter={TextColumnFilter}
                order={orderOptions}
              />

              <Table.Th
                title={i18n.t.common.responsibleCode}
                fieldName="carPlanning.manager.name"
                filters={filterOptions}
                filter={TextColumnFilter}
                order={orderOptions}
              />

              <Table.Th
                title={i18n.t.common.type}
                fieldName="carPlanning.type"
                filters={filterOptions}
                filter={TextColumnFilter}
                order={orderOptions}
                formatter={(value) => types.find((x) => x.value == value)?.label}
              />
              <Table.Th title={i18n.t.common.year} fieldName="carPlanning.year" order={orderOptions} />

              <Table.Th title={i18n.t.common.startDate} fieldName="carPlanning.startDate" order={orderOptions} />
              <Table.Th
                title={i18n.t.common.plannedArrivalDate}
                fieldName="carPlanning.carArrivalDate"
                order={orderOptions}
              />

              <Table.Th
                align="right"
                title={i18n.t.common.priceWithoutVat}
                fieldName="carPlanning.priceWithoutVat"
                order={orderOptions}
              />
              <Table.Th
                title={i18n.t.common.state}
                fieldName="carPlanning.status"
                filters={filterOptions}
                filter={TextColumnFilter}
                order={orderOptions}
                formatter={(value) => carPlanningStates.find((x) => x.value == value)?.label}
              />
              <Table.Th
                title={i18n.t.common.order}
                fieldName="carPlanning.carPurchase.state"
                filters={filterOptions}
                filter={TextColumnFilter}
                order={orderOptions}
                formatter={(value) => carPurchaseStates.find((x) => x.value == value)?.label}
              />
              <th></th>
            </>
          }
          onRowClick={(item) => navigate("/admin/car_plannings/" + item.id)}
          renderRow={(item) => (
            <>
              <td>
                {isWithinLast48h(item.createdAt) && <NewItemIcon />}
                {item.nr}
              </td>
              <td>{item.driverName}</td>
              <Table.Td error={!item.subsidiary?.semiramisOrganizationCode}>
                {!item.subsidiary?.semiramisOrganizationCode ? (
                  <Tooltip message={i18n.t.subsidiaries.errors.notMatchingSubsidiaryFound}>
                    {item.subsidiary ? item.subsidiary.name : item.subsidiaryCode}
                  </Tooltip>
                ) : (
                  <>{item.subsidiary ? item.subsidiary.name : item.subsidiaryCode}</>
                )}
              </Table.Td>
              <Table.Td error={!item.subsidiary?.semiramisOrganizationCode}>
                {!item.subsidiary?.semiramisOrganizationCode ? (
                  <Tooltip message={i18n.t.subsidiaries.errors.notMatchingSubsidiaryFound}>
                    {item.subsidiaryCode}
                  </Tooltip>
                ) : (
                  <>{item.subsidiaryCode}</>
                )}
              </Table.Td>
              <Table.Td error={!item.costCenterExists}>
                {!item.costCenterExists ? (
                  <Tooltip message={i18n.t.users.errors.noMatchingUserFound}>{item.costCenter}</Tooltip>
                ) : (
                  <>{item.costCenter}</>
                )}
              </Table.Td>
              <Table.Td>{item.managerName}</Table.Td>
              <td>{types.find((x) => x.value == item.type)?.label}</td>
              <td>{item.year}</td>
              <td>{item.startDate && formatDateNumeric(item.startDate, i18n.language)}</td>
              <td>{item.carArrivalDate && formatDateNumeric(item.carArrivalDate, i18n.language)}</td>
              <td align="right">{!!item.priceWithoutVat && formatCurrency(item.priceWithoutVat, i18n.language)}</td>
              <td>
                {item.status && (
                  <State
                    text={carPlanningStates.find((x) => x.value == item.status)?.label!}
                    color={statesMap[item.status]}
                  />
                )}
              </td>
              <td>
                {item.carPurchase ? (
                  <State
                    text={carPurchaseStates.find((x) => x.value == item.carPurchase?.state)?.label!}
                    color={
                      item.carPurchase?.state == "assigned"
                        ? "green"
                        : item.carPurchase?.state == "arrived"
                          ? "orange"
                          : "red"
                    }
                  />
                ) : undefined}
              </td>
            </>
          )}
          rowActions={getRowActions}
          error={result.error}
          items={data?.items}
          total={data?.total}
        />
      </Panel>

      {!!itemToDelete && (
        <DeleteCarPlanningDialog carPlanning={itemToDelete} onClose={() => setItemToDelete(undefined)} />
      )}

      {!!itemToShowVersions && (
        <CarPlanningHistoryDialog
          itemToShowVersions={itemToShowVersions}
          onClose={() => setItemToShowVersions(undefined)}
        />
      )}

      {!!openNewCarDriver && (
        <ModalDialog onClose={() => setOpenNewCarDriver(undefined)} title={i18n.t.carDrivers.new}>
          <CarDriverForm isDisabled={isSubmitting} onSubmit={onSubmit} />
        </ModalDialog>
      )}
    </>
  );
};

export default CarPlannings;
